import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import {
  FaSearch, FaTimes, FaSignOutAlt, FaStar, FaRegStar,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import {
  TEAMS, HOME, TEAMS_HUB, AGENT_NAV, AGENDA_NAV, canSee, getFrequentSections, togglePin, isPinned,
} from '../../config/teams';
import { useCommandPalette } from '../common/CommandPalette';
import { useCrisis } from '../../contexts/CrisisContext';

/* ============================================================
   Tokens locales
   ============================================================ */

const accent = 'var(--btn-primary)';
const borderSoft = 'var(--border-soft)';
const mono = 'var(--font-mono)';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(6, 214, 160, 0.55); }
  50%     { box-shadow: 0 0 0 5px rgba(6, 214, 160, 0); }
`;

/* ============================================================
   Container
   ============================================================ */

const SidebarContainer = styled.aside`
  width: 270px;
  height: 100vh;
  position: fixed;
  top: 0;
  left: ${({ $isOpen }) => ($isOpen ? '0' : '-280px')};
  z-index: var(--z-sidebar);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background:
    linear-gradient(180deg, rgba(0, 180, 216, 0.06), transparent 30%),
    radial-gradient(800px 400px at 0% 0%, rgba(0, 180, 216, 0.05), transparent 65%),
    var(--color-primary);
  border-right: 1px solid ${borderSoft};
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.32);
  transition: left 0.28s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.018) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.018) 1px, transparent 1px);
    background-size: 22px 22px;
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.7), transparent 60%);
  }

  @media (max-width: 1024px) { width: 250px; }
  @media (max-width: 768px) {
    width: 84vw;
    max-width: 320px;
    left: ${({ $isOpen }) => ($isOpen ? '0' : '-100%')};
    box-shadow: ${({ $isOpen }) => ($isOpen ? '4px 0 40px rgba(0,0,0,0.6)' : 'none')};
  }
`;

const MobileCloseButton = styled.button`
  display: none;
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(11, 19, 32, 0.7);
  border: 1px solid ${borderSoft};
  color: var(--text-secondary);
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
  cursor: pointer;
  z-index: 10;

  &:hover { color: var(--alert-error); border-color: rgba(239, 71, 111, 0.4); }

  @media (max-width: 768px) {
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    align-items: center;
    justify-content: center;
  }
`;

/* ============================================================
   Header
   ============================================================ */

const Header = styled.header`
  position: relative;
  padding: 1.1rem 1.1rem 0.9rem;
  border-bottom: 1px solid ${borderSoft};
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  text-decoration: none;
`;

const Logo = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
  border-radius: var(--radius-sm);
  background: rgba(0, 180, 216, 0.08);
  padding: 4px;
  border: 1px solid rgba(0, 180, 216, 0.25);
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  .name {
    font-size: 0.96rem;
    font-weight: 600;
    color: var(--text-primary);
    letter-spacing: 0.02em;
    line-height: 1.1;
  }

  .tag {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: ${accent};
    margin-top: 3px;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.85rem;
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
`;

const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--alert-success);
  animation: ${pulse} 2.2s ease-in-out infinite;
`;

const StatusVer = styled.span`
  margin-left: auto;
  color: var(--text-disabled);
`;

/* ============================================================
   Buscador (dispara el command palette)
   ============================================================ */

const SearchTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  width: calc(100% - 2rem);
  margin: 0.85rem 1rem 0.2rem;
  padding: 0.55rem 0.7rem;
  background: rgba(11, 19, 32, 0.55);
  border: 1px solid ${borderSoft};
  border-radius: var(--radius-sm);
  color: var(--text-disabled);
  font-family: inherit;
  font-size: 0.84rem;
  cursor: pointer;
  transition: border-color 0.16s ease, color 0.16s ease;

  > svg { font-size: 0.78rem; }
  .label { flex: 1; text-align: left; }
  kbd {
    font-family: ${mono};
    font-size: 0.58rem;
    letter-spacing: 0.06em;
    border: 1px solid ${borderSoft};
    border-radius: 4px;
    padding: 1px 5px;
    color: var(--text-disabled);
  }

  &:hover {
    border-color: rgba(0, 180, 216, 0.45);
    color: var(--text-secondary);
  }
`;

/* ============================================================
   Menu
   ============================================================ */

const Menu = styled.nav`
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
  padding: 0.5rem 0.65rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.12rem;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.08); border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.16); }
`;

const NavLink = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.5rem 0.75rem;
  margin: 0 0.2rem;
  border-radius: 7px;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.88rem;
  letter-spacing: 0.01em;
  transition: color 0.16s ease, background 0.16s ease;
  border: 1px solid transparent;

  &::before {
    content: '';
    position: absolute;
    left: -0.2rem;
    top: 50%;
    transform: translateY(-50%) scaleY(${({ $active }) => ($active ? 1 : 0)});
    width: 3px;
    height: 60%;
    background: ${accent};
    border-radius: 2px;
    transition: transform 0.2s ease;
    box-shadow: 0 0 8px rgba(0, 180, 216, 0.55);
  }

  ${({ $active }) =>
    $active
      ? `
    color: var(--text-primary);
    background: linear-gradient(90deg, rgba(0, 180, 216, 0.14), rgba(0, 180, 216, 0));
    border-color: rgba(0, 180, 216, 0.18);
  `
      : `
    &:hover {
      color: var(--text-primary);
      background: rgba(255, 255, 255, 0.025);
      border-color: rgba(255, 255, 255, 0.04);
    }
  `}

  &:hover .pin { opacity: 0.65; }
`;

const NavIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
  background: ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.18)' : 'rgba(11, 19, 32, 0.5)')};
  color: ${({ $active, $color }) => ($active ? accent : $color || 'var(--text-secondary)')};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.4)' : borderSoft)};
  flex-shrink: 0;
  transition: all 0.16s ease;

  ${NavLink}:hover & {
    color: ${accent};
    border-color: rgba(0, 180, 216, 0.3);
    background: rgba(0, 180, 216, 0.08);
  }
`;

const NavLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NavCount = styled.span`
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  color: ${(p) => (p.$tone === 'danger' ? 'var(--alert-error)' : accent)};
  background: ${(p) => (p.$tone === 'danger' ? 'rgba(239, 71, 111, 0.12)' : 'rgba(0, 180, 216, 0.08)')};
  border: 1px solid ${(p) => (p.$tone === 'danger' ? 'rgba(239, 71, 111, 0.45)' : 'rgba(0, 180, 216, 0.3)')};
  padding: 1px 6px;
  border-radius: 3px;
`;

const PinBtn = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: ${({ $on }) => ($on ? 'var(--alert-warning)' : 'var(--text-disabled)')};
  opacity: ${({ $on }) => ($on ? 1 : 0)};
  font-size: 0.72rem;
  transition: opacity 0.15s ease, color 0.15s ease;
  &:hover { color: var(--alert-warning); opacity: 1 !important; }
`;

/* Encabezado de grupo (acordeón) */
const CategoryBtn = styled.button`
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-disabled);
  padding: 0.85rem 0.85rem 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.15s ease;

  &::before {
    content: '';
    display: inline-block;
    width: 5px;
    height: 5px;
    background: ${({ $color }) => $color || accent};
    border-radius: 50%;
    box-shadow: 0 0 0 2px rgba(0, 180, 216, 0.14);
  }
  &:hover { color: var(--text-secondary); }
`;
const GroupCount = styled.span`
  font-size: 0.58rem;
  color: var(--text-disabled);
  opacity: 0.6;
`;
const Chevron = styled.span`
  margin-left: auto;
  font-size: 0.62rem;
  opacity: 0.55;
  transition: transform 0.18s ease;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
`;

/* ============================================================
   Footer
   ============================================================ */

const Footer = styled.div`
  flex-shrink: 0;
  padding: 0.85rem 1rem 1rem;
  border-top: 1px solid ${borderSoft};
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.18));
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.65rem;
  background: rgba(11, 19, 32, 0.6);
  border: 1px solid ${borderSoft};
  border-radius: 7px;
  margin-bottom: 0.55rem;
`;

const Avatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: var(--radius-sm);
  background: rgba(0, 180, 216, 0.12);
  border: 1px solid rgba(0, 180, 216, 0.32);
  color: ${accent};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${mono};
  font-size: 0.78rem;
  font-weight: 600;
  flex-shrink: 0;
`;

const UserText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;

  .name {
    font-size: 0.83rem;
    color: var(--text-primary);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    line-height: 1.1;
  }
  .meta {
    font-family: ${mono};
    font-size: 0.58rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-top: 2px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  width: 100%;
  background: transparent;
  border: 1px solid ${borderSoft};
  color: var(--text-secondary);
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-sm);
  font-family: inherit;
  font-size: 0.8rem;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;

  svg { font-size: 0.8rem; }
  &:hover {
    color: var(--alert-error);
    border-color: rgba(239, 71, 111, 0.4);
    background-color: rgba(239, 71, 111, 0.06);
  }
`;

/* ============================================================
   Datos del menú
   ============================================================ */

// Navegación fija arriba: Inicio (home) y Equipos (hub).
const TOP = [HOME, TEAMS_HUB, AGENT_NAV, AGENDA_NAV];

function getInitials(text) {
  if (!text) return '??';
  const clean = String(text).split('@')[0].replace(/[^\p{L}\p{N}\s.-]/gu, ' ');
  const parts = clean.split(/[\s._-]+/).filter(Boolean);
  return (parts[0]?.[0] || '?') + (parts[1]?.[0] || '');
}

const OPEN_KEY = 'av_sidebar_open_groups';

const Sidebar = ({ $isOpen, toggleSidebar }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { open: openPalette } = useCommandPalette();
  const role = user?.role || 'admin';

  // Frecuentes — se recalcula al navegar (pathname cambia) o al fijar.
  const [pinTick, setPinTick] = useState(0);
  const frequents = useMemo(
    () => getFrequentSections(role, 6),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role, location.pathname, pinTick]
  );

  // Acordeón: varias áreas pueden estar abiertas; el estado se guarda.
  // Por defecto arranca abierta el área de la ruta activa.
  const activeTeam = TEAMS.find((t) =>
    t.tools.some((i) => location.pathname === i.to || location.pathname.startsWith(`${i.to}/`))
  );
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(OPEN_KEY) || 'null');
      if (saved) return saved;
    } catch { /* noop */ }
    return activeTeam ? { [activeTeam.id]: true } : {};
  });

  const toggleGroup = (id) =>
    setOpenGroups((p) => {
      const next = { ...p, [id]: !p[id] };
      try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });

  // Al cambiar de ruta, asegurar que el área activa esté abierta.
  useEffect(() => {
    if (activeTeam && !openGroups[activeTeam.id]) {
      setOpenGroups((p) => ({ ...p, [activeTeam.id]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLinkClick = () => {
    if (window.innerWidth <= 768) toggleSidebar();
  };

  const handlePin = (e, to) => {
    e.preventDefault();
    e.stopPropagation();
    togglePin(to);
    setPinTick((n) => n + 1);
  };

  const isActive = (to, exact) => {
    if (exact) return location.pathname === to;
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  const visibleTeams = useMemo(
    () => TEAMS.map((t) => ({ ...t, tools: t.tools.filter((i) => canSee(i.to, role)) }))
      .filter((t) => t.tools.length > 0),
    [role]
  );

  const { crisis } = useCrisis();

  const userLabel = user?.name || user?.email || 'Invitado';
  const userMeta = user?.email || 'sesión local';
  const initials = getInitials(user?.name || user?.email).toUpperCase();
  const year = new Date().getFullYear();

  const renderItem = (item, { withPin = false } = {}) => {
    const active = isActive(item.to, item.exact);
    const Icon = item.icon;
    const pinned = withPin && isPinned(item.to);
    // Mientras hay una crisis abierta, la Sala de Crisis pasa a badge rojo con
    // la gravedad, para que se vea desde cualquier pantalla sin abrir nada.
    const enCrisis = crisis && item.to === '/urgencia';
    const badge = enCrisis ? String(crisis.severidad).toUpperCase() : item.badge;
    return (
      <NavLink key={`${item.to}-${withPin ? 'f' : 'm'}`} to={item.to} $active={active} onClick={handleLinkClick}>
        <NavIcon $active={active} $color={item.teamColor}><Icon /></NavIcon>
        <NavLabel>{item.label}</NavLabel>
        {badge && <NavCount $tone={enCrisis ? 'danger' : undefined}>{badge}</NavCount>}
        {withPin && (
          <PinBtn
            className="pin"
            $on={pinned}
            role="button"
            aria-label={pinned ? 'Quitar de frecuentes' : 'Fijar en frecuentes'}
            onClick={(e) => handlePin(e, item.to)}
          >
            {pinned ? <FaStar /> : <FaRegStar />}
          </PinBtn>
        )}
      </NavLink>
    );
  };

  return (
    <SidebarContainer $isOpen={$isOpen} onClick={(e) => e.stopPropagation()}>
      <MobileCloseButton $isOpen={$isOpen} onClick={toggleSidebar} aria-label="Cerrar menú">
        <FaTimes />
      </MobileCloseButton>

      <Header>
        <Brand to="/" onClick={handleLinkClick}>
          <Logo src="/logoarcana.png" alt="Arcana Vision" />
          <BrandText>
            <span className="name">Arcana Vision</span>
            <span className="tag">OPS · Campaña {year}</span>
          </BrandText>
        </Brand>
        <StatusRow>
          <StatusDot />
          Online
          <StatusVer>en vivo</StatusVer>
        </StatusRow>
      </Header>

      <SearchTrigger type="button" onClick={openPalette} aria-label="Buscar una sección">
        <FaSearch />
        <span className="label">Buscar sección…</span>
        <kbd>⌘K</kbd>
      </SearchTrigger>

      <Menu>
        {/* Navegación fija */}
        {TOP.map((item) => renderItem(item))}

        {/* Frecuentes */}
        {frequents.length > 0 && (
          <>
            <CategoryBtn as="div" style={{ cursor: 'default' }}>
              <FaStar style={{ fontSize: '0.6rem', marginLeft: '-2px' }} /> Frecuentes
              <GroupCount>{frequents.length}</GroupCount>
            </CategoryBtn>
            {frequents.map((item) => renderItem(item, { withPin: true }))}
          </>
        )}

        {/* Áreas */}
        {visibleTeams.map((team) => {
          const open = !!openGroups[team.id];
          return (
            <React.Fragment key={team.id}>
              <CategoryBtn
                type="button"
                $color={team.color}
                onClick={() => toggleGroup(team.id)}
                aria-expanded={open}
              >
                {team.name}
                <GroupCount>{team.tools.length}</GroupCount>
                <Chevron $open={open}>▸</Chevron>
              </CategoryBtn>
              {open && team.tools.map((item) => renderItem({ ...item, teamColor: team.color }))}
            </React.Fragment>
          );
        })}
      </Menu>

      <Footer>
        {user && (
          <UserCard>
            <Avatar>{initials}</Avatar>
            <UserText>
              <span className="name" title={userLabel}>{userLabel}</span>
              <span className="meta" title={userMeta}>{userMeta}</span>
            </UserText>
          </UserCard>
        )}
        <LogoutButton type="button" onClick={logout} aria-label="Cerrar sesión">
          <FaSignOutAlt /> Cerrar sesión
        </LogoutButton>
      </Footer>
    </SidebarContainer>
  );
};

export default Sidebar;
